import React from 'react'
import { Section, SectionTitle, SubSection, SubSectionTitle, Text, List, Table, CodeBlock } from '../styles'
import APIDocSection from './APIDocSection'
import DeveloperDashboardSection from './DeveloperDashboardSection'

export default function ImplementationSection() {
  return (
    <Section id="implementation">
      <SectionTitle>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
        </svg>
        7. Implementation Details
      </SectionTitle>
      
      <SubSection id="frontend">
        <SubSectionTitle>7.1 Frontend Architecture</SubSectionTitle>
        <Text>
          The frontend architecture is built with modern React patterns and
          optimized for performance and maintainability:
        </Text>
        <CodeBlock>
{`// Component Architecture Example
import React, { Suspense } from 'react'
import { ErrorBoundary } from './ErrorBoundary'
import { useAgent } from './hooks/useAgent'
import { useProject } from './hooks/useProject'

// Lazy-loaded components
const NetworkVisualization = React.lazy(() => 
  import('./components/NetworkVisualization')
)
const ProjectDashboard = React.lazy(() => 
  import('./components/ProjectDashboard')
)

function AgentDashboard({ projectId }) {
  const { agents, metrics } = useAgent(projectId)
  const { project } = useProject(projectId)

  return (
    <div className="dashboard">
      <ErrorBoundary fallback={<ErrorView />}>
        <Suspense fallback={<LoadingSpinner />}>
          <NetworkVisualization
            agents={agents}
            connections={project.connections}
            metrics={metrics}
          />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary fallback={<ErrorView />}>
        <Suspense fallback={<LoadingSpinner />}>
          <ProjectDashboard
            project={project}
            metrics={metrics}
            onUpdate={handleUpdate}
          />
        </Suspense>
      </ErrorBoundary>
    </div>
  )
}`}
        </CodeBlock>
      </SubSection>

      <SubSection id="backend">
        <SubSectionTitle>7.2 Backend Services</SubSectionTitle>
        <Text>
          Our backend infrastructure is built on microservices architecture
          with specialized services for different functionalities:
        </Text>
        <Table>
          <thead>
            <tr>
              <th>Service</th>
              <th>Technology</th>
              <th>Responsibility</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>API Gateway</td>
              <td>Node.js + Express</td>
              <td>Request routing and authentication</td>
            </tr>
            <tr>
              <td>Agent Service</td>
              <td>Python + FastAPI</td>
              <td>AI model inference and coordination</td>
            </tr>
            <tr>
              <td>Project Service</td>
              <td>Rust + Actix</td>
              <td>Project state and business logic</td>
            </tr>
            <tr>
              <td>Analytics Service</td>
              <td>Go + gRPC</td>
              <td>Metrics collection and analysis</td>
            </tr>
            <tr>
              <td>Blockchain Service</td>
              <td>Rust + Solana SDK</td>
              <td>Smart contract interaction</td>
            </tr>
          </tbody>
        </Table>
      </SubSection>

      <SubSection id="api">
        <SubSectionTitle>7.3 API Integration</SubSectionTitle>
        <APIDocSection />
        <DeveloperDashboardSection />
      </SubSection>

      <SubSection id="testing">
        <SubSectionTitle>7.4 Testing Strategy</SubSectionTitle>
        <Text>
          Our comprehensive testing strategy ensures reliability across all
          system components:
        </Text>
        <List>
          <li>
            Unit Testing:
            <ul>
              <li>Component isolation with Jest and Testing Library</li>
              <li>Mock service workers for API simulation</li>
              <li>Snapshot testing for UI components</li>
              <li>State management tests</li>
              <li>Utility function coverage</li>
            </ul>
          </li>
          <li>
            Integration Testing:
            <ul>
              <li>API endpoint integration tests</li>
              <li>Service communication verification</li>
              <li>Database interaction tests</li>
              <li>Event handling and propagation</li>
              <li>Cross-service workflows</li>
            </ul>
          </li>
          <li>
            End-to-End Testing:
            <ul>
              <li>User journey simulations</li>
              <li>Browser automation with Cypress</li>
              <li>Mobile responsiveness testing</li>
              <li>Performance benchmarking</li>
              <li>Load testing scenarios</li>
            </ul>
          </li>
          <li>
            Smart Contract Testing:
            <ul>
              <li>Contract deployment tests</li>
              <li>Transaction validation</li>
              <li>State transition verification</li>
              <li>Gas optimization checks</li>
              <li>Security vulnerability scanning</li>
            </ul>
          </li>
        </List>
      </SubSection>
    </Section>
  )
}
